import React, { useEffect, useRef, useState } from 'react';

import AddPaymentMethod from 'components/payment-methods/AddPaymentMethod';
import usePaymentMethods from 'hooks/paymentMethod/usePaymentMethods';
import notificationHandler from 'utils/notifications';

interface AsyncAddPaymentMethodProps {
  athleteId: string;
  gymId: string;
  successHandler: (paymentMethodId: string) => void;
  cancelHandler: () => void;
  /** Forwarded to AddPaymentMethod: this wrapper also serves the sales-portal checkout. */
  isStaffContext?: boolean;
}

const AsyncAddPaymentMethod = ({
  athleteId,
  gymId,
  successHandler,
  cancelHandler,
  isStaffContext,
}: AsyncAddPaymentMethodProps): JSX.Element => {
  const { errorNotification } = notificationHandler();

  const [stripePaymentMethodId, setStripePaymentMethodId] = useState<string>('');
  const [pollEnabled, setPollEnabled] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: paymentMethodData,
    isSuccess: isPaymentMethodSuccess,
    isError: isPaymentMethodError,
  } = usePaymentMethods({
    athleteId: athleteId,
    stripePaymentMethodId: stripePaymentMethodId,
    refetchInterval: 2000,
    enabled: pollEnabled && !!stripePaymentMethodId,
  });

  useEffect(() => {
    if (!stripePaymentMethodId) return;

    setPollEnabled(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setPollEnabled(false);
      errorNotification(
        'Payment method creation has failed',
        'This payment method cannot be found in our records.',
      );
    }, 30000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [stripePaymentMethodId, errorNotification]);

  useEffect(() => {
    if (isPaymentMethodError) {
      setPollEnabled(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;

      errorNotification(
        'Payment method creation has failed',
        'This payment method cannot be found in our records.',
      );
    }
  }, [isPaymentMethodError, errorNotification]);

  useEffect(() => {
    if (isPaymentMethodSuccess && paymentMethodData && paymentMethodData.pages[0].data.results.length) {
      setPollEnabled(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;

      successHandler(paymentMethodData.pages[0].data.results[0].paymentMethodId);
    }
  }, [athleteId, isPaymentMethodSuccess, paymentMethodData, successHandler]);

  return (
    <AddPaymentMethod
      athleteId={athleteId}
      gymId={gymId}
      isStaffContext={isStaffContext}
      successHandler={value => setStripePaymentMethodId(value || '')}
      cancelHandler={cancelHandler}
    />
  );
};

export default AsyncAddPaymentMethod;
